import React from "react";
import { IconButton, Tooltip, TablePagination } from "@mui/material";
import { Edit, DeleteOutline } from "@mui/icons-material";
import CommonTable, { HeadCell } from "../../../../../business-types/grocery/components/CommonTable";
import { Product, Category } from "../types/productTypes";
import Image from "src/shared/Image";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";

type ProductRow = {
  id: string;
  sno: number;
  ProductName: string;
  ArabicName: string;
  Amount: string;
  CategoryName: string;
  Image: JSX.Element;
  Actions: JSX.Element;
};

interface ProductTableProps {
  products: Product[];
  categories: Category[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  showDeletedProducts: boolean;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  categories,
  loading,
  page,
  rowsPerPage,
  totalRecords,
  showDeletedProducts,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
}) => {
  const getCategoryName = (categoryId: string): string => categories.find((cat) => cat._id.$oid === categoryId)?.CategoryName || "Unknown";

  const formatAmount = (amount: any): string => {
    const num = Number(amount);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const rows: ProductRow[] = products.map((item, index) => ({
    id: item._id.$oid,
    sno: page * rowsPerPage + index + 1,
    ProductName: item.ProductName,
    ArabicName: item.ArabicName || "-",
    Amount: formatAmount(item.Amount),
    CategoryName: getCategoryName(item.category_id),

    Image: <Image imagePath={item.image_url} className='w-[60px] h-[60px] flex justify-center items-center' />,
    Actions: (
      <>
        <Tooltip title='Edit' placement='bottom' arrow>
          <span>
            <IconButton disabled={showDeletedProducts} onClick={() => onEdit(item)} sx={{ color: showDeletedProducts ? "gray" : "black" }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title='Delete' placement='bottom' arrow>
          <span>
            <IconButton disabled={showDeletedProducts} onClick={() => onDelete(item)} sx={{ color: showDeletedProducts ? "gray" : "red" }}>
              <DeleteOutline />
            </IconButton>
          </span>
        </Tooltip>
      </>
    ),
  }));

  // Define table headers
  const headCells: HeadCell<ProductRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "ProductName", label: "Product Name" },
    { id: "ArabicName", label: "Arabic Name" },
    { id: "Amount", label: "Price (QAR)", numeric: true, align: "center" },
    { id: "CategoryName", label: "Category" },
    { id: "Image", label: "Image" },
    { id: "Actions", label: "Actions", align: "center" },
  ];

  return (
    <div className='w-full overflow-y-auto text-gray-900'>
      <CommonTable<ProductRow>
        // key={rowsPerPage}
        data={rows}
        headCells={headCells}
        showToolbar={false}
        denseDefault={false}
        title='Products'
        pageSize={rowsPerPage}
        pagenation={false}
        selectable={false}
      />

      <div className='flex justify-end mr-8 mt-2 text-gray-900'>
        <TablePagination
          component='div'
          count={totalRecords}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onRowsPerPageChange(newSize);
            onPageChange(null, 0);
          }}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        />
      </div>
    </div>
  );
};

export default ProductTable;
